import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';

const BarChart = ({
  data = [],
  labelKey,
  valueKey,
  height = 280,
  seriesName = 'Value',
  color = '#26A69A',
  horizontal = false,
}) => {
  const chartConfig = useMemo(() => {
    const categories = data.map(item => item?.[labelKey] ?? '');
    const seriesData = data.map(item => Number(item?.[valueKey] ?? 0));

    return {
      options: {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        plotOptions: {
          bar: {
            horizontal,
            columnWidth: horizontal ? '50%' : '42%',
            borderRadius: 10,
            distributed: false,
            dataLabels: {
              position: 'top',
            },
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: horizontal ? 0 : -18,
          formatter: val => (Number.isFinite(val) ? Math.round(val) : val),
          style: {
            fontSize: '11px',
            fontWeight: 600,
            colors: ['#475569'],
          },
        },
        xaxis: {
          categories,
          labels: {
            rotate: -40,
            style: { fontSize: '11px' },
            trim: false,
          },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            formatter: val => (Number.isFinite(val) ? Math.round(val) : val),
          },
        },
        tooltip: {
          y: {
            formatter: val => (Number.isFinite(val) ? val.toLocaleString() : val),
          },
        },
        colors: [color],
        grid: {
          borderColor: 'rgba(148, 163, 184, 0.2)',
          strokeDashArray: 4,
        },
      },
      series: [
        {
          name: seriesName,
          data: seriesData,
        },
      ],
    };
  }, [data, labelKey, valueKey, color, seriesName, horizontal]);

  if (!data?.length) {
    return (
      <div style={{ height }} className="flex items-center justify-center text-sm text-gray-500">
        No data available
      </div>
    );
  }

  return (
    <ReactApexChart
      options={chartConfig.options}
      series={chartConfig.series}
      type="bar"
      height={height}
    />
  );
};

BarChart.propTypes = {
  data: PropTypes.array,
  labelKey: PropTypes.string.isRequired,
  valueKey: PropTypes.string.isRequired,
  height: PropTypes.number,
  seriesName: PropTypes.string,
  color: PropTypes.string,
  horizontal: PropTypes.bool,
};

export default BarChart;